import { Routes } from '@angular/router';
import { AuthGuard } from '@core/guard/auth.guard';
import { PermissionGuard } from '@core/guard/permission.guard';
import { LogoutComponent } from './features/logout/logout.component';
import { Page404Component } from './features/page404/page404.component';
import { AuthLayoutComponent } from './layout/app-layout/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layout/app-layout/main-layout/main-layout.component';
import { PermissionType } from '@core/enums/permission-type.enum';
import { NoDapClientGuard } from '@core/guard/no-dap-client.guard';
import { NoDapReactivateGuard } from '@core/guard/no-dap-reactivate.guard';
import { UserPhotoResolver } from './layout/app-layout/main-layout/user-photo.resolver';

export const APP_ROUTE: Routes = [
    {
        path: '',
        component: MainLayoutComponent,
        resolve: { resolverData: UserPhotoResolver },
        canActivate: [AuthGuard, NoDapClientGuard],
        children: [
            {
                path: '',
                redirectTo: '/home',
                pathMatch: 'full',
            },
            {
                path: 'dashboard',
                data: {
                    pageTitle: 'feature.dashboard.page.title',
                    requiredPermissionTypes: [],
                },
                loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
                canActivate: [PermissionGuard],
            },
            {
                path: 'my-contract',
                data: {
                    pageTitle: 'feature.mycontract.main.title',
                    requiredPermissionTypes: [
                        PermissionType.CLIENT_REGISTRATION,
                        PermissionType.CONTRACT_HANDLER,
                        PermissionType.DOCUMENT_DOWNLOADER,
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/my-contract/my-contract.routes').then( (m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'new-upload',
                data: {
                    pageTitle: 'feature.newupload.page.title',
                    requiredPermissionTypes: [
                        PermissionType.UPLOAD,
                        PermissionType.C2PA_SIGN,
                    ],
                    anyPermissionTypes: [],
                    prohibitedPermissionTypes: [
                        PermissionType.NO_SIGNED_CONTRACT_CLIENT,
                    ],
                },
                loadChildren: () => import('./features/new-upload/new-upload.routes').then( (m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'uploaded-content',
                data: {
                    pageTitle: 'feature.uploadedcontent.search.title',
                    requiredPermissionTypes: [],
                    anyPermissionTypes: [
                        PermissionType.UPLOAD_READ_ALL,
                        PermissionType.UPLOAD_READ,
                    ],
                    prohibitedPermissionTypes: [
                        PermissionType.NO_SIGNED_CONTRACT_CLIENT,
                    ]
                },
                loadChildren: () => import('./features/uploaded-content/uploaded-content.routes').then( (m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'integrity-check',
                data: {
                    pageTitle: 'feature.integritycheck.page.title',
                    requiredPermissionTypes: [
                        PermissionType.C2PA_VALIDATION,
                        PermissionType.BLOCK_VALIDATION,
                    ],
                    anyPermissionTypes: [],
                    prohibitedPermissionTypes: [
                        PermissionType.NO_SIGNED_CONTRACT_CLIENT,
                    ]
                },
                loadChildren: () => import('./features/integrity-check/integrity-check.routes').then( (m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'users',
                data: {
                    pageTitle: 'feature.usermanagement.search.title',
                    requiredPermissionTypes: [
                        PermissionType.USER_READ,
                        PermissionType.ORG_READ,
                        PermissionType.ROLE_READ,
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/user-management/user-management.routes').then( (m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'organizations',
                data: {
                    pageTitle: 'feature.organizations.search.title',
                    requiredPermissionTypes: [
                        PermissionType.ORG_WRITE,
                        PermissionType.USER_READ, 
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/organizations/organizations.routes').then( (m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'roles',
                data: {
                    pageTitle: 'feature.roles.page.title',
                    requiredPermissionTypes: [
                        PermissionType.ROLE_READ,
                        PermissionType.ROLE_WRITE,
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/roles/roles.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'contract',
                data: {
                    pageTitle: 'feature.contract.page.title',
                    requiredPermissionTypes: [
                        PermissionType.CONTRACT_HANDLER,
                        // PermissionType.CONTRACT_UPLOADER,
                    ],
                    anyPermissionTypes: [],
                    prohibitedPermissionTypes:[
                        PermissionType.CLIENT_REGISTRATION,
                    ]
                },
                loadChildren: () => import('./features/contract/contract.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'activity-log',
                data: {
                    pageTitle: 'feature.activityLog.page.title',
                    requiredPermissionTypes: [
                        PermissionType.ACTIVITY_LOG
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/audit-log/audit-log.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'statistics',
                data: {
                    pageTitle: 'feature.statistics.page.title',
                    requiredPermissionTypes: [
                        PermissionType.STATISTICS
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/statistics/statistics.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'blockchain',
                data: {
                    pageTitle: 'feature.blockchain.blockchainlist.title',
                    requiredPermissionTypes: [
                        PermissionType.BLOCKCHAIN_VALIDATION,
                        PermissionType.BLOCK_VALIDATION,
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/blockchain/blockchain.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'block-checking',
                data: {
                    pageTitle: 'feature.blockchain.blockchecking.title',
                    requiredPermissionTypes: [
                        PermissionType.BLOCKCHAIN_VALIDATION,
                        PermissionType.BLOCK_VALIDATION,
                    ],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/blockchain/blockchain.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'document-library',
                data: {
                    pageTitle: 'feature.documentlibrary.search.title',
                    requiredPermissionTypes: [],
                    anyPermissionTypes: [
                        PermissionType.DOCUMENT_MAINTAINER,
                        PermissionType.CLIENT_REGISTRATION,
                    ]
                },
                loadChildren: () => import('./features/document-library/document-library.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
            {
                path: 'profile',
                data: {
                    pageTitle: 'feature.profile.title',
                    requiredPermissionTypes: [],
                    anyPermissionTypes: [],
                },
                loadChildren: () => import('./features/profile/profile.routes').then((m) => m.routes),
                canActivate: [PermissionGuard],
            },
        ],
    },
    {
        path: 'login',
        component: AuthLayoutComponent,
        loadChildren: () => import('./features/login/login.routes').then((m) => m.routes),
    },
    {
        path: 'dap-login',
        component: AuthLayoutComponent,
        loadChildren: () => import('./features/dap-login/dap-login.routes').then((m) => m.routes), // DÁP login
    },
    {
        path: 'reactivate',
        component: AuthLayoutComponent,
        loadChildren: () => import('./features/reactivate/reactivate.routes').then((m) => m.routes),
        canActivate: [AuthGuard, NoDapReactivateGuard],
    },
    {
        path: 'registration/:sessionId/:dapId',
        component: AuthLayoutComponent,
        loadChildren: () => import('./features/registration/registration.routes').then((m) => m.routes),
    },
    {
        path: 'forgotten-password',
        component: AuthLayoutComponent,
        loadChildren: () => import('./features/forgotten-password/forgotten-password.routes').then(m => m.routes),
    },
    {
        path: 'set-password',
        component: AuthLayoutComponent,
        loadChildren: () => import('./features/new-password/new-password.routes').then(m => m.routes),
    },
    {
        path: 'authenticator',
        component: AuthLayoutComponent,
        canActivate: [AuthGuard, NoDapClientGuard],
        loadChildren: () => import('./features/authenticator/authenticator.routes').then(m => m.routes),
    },
    {
        path: 'home',
        component: AuthLayoutComponent,
        loadChildren: () => import('./features/home/home.routes').then((m) => m.routes),
    },
    {
        path: 'logout',
        component: LogoutComponent,
    },
    {
        path: '**',
        component: Page404Component,
    },
];
